/** @jsxImportSource @emotion/react */
import { ChangeEvent, useMemo } from 'react'
import Button from 'react-bootstrap/Button'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'

import states from 'constants/states'
import { phoneValidation, requiredPhoneValidation } from 'helpers/validate'
import MaskInput from 'components/atoms/MaskInput'
import InputMask from 'components/atoms/InputMask'
import GoogleAutocomplete from 'components/atoms/GoogleAutocomplete'
import { TextAreaInput } from 'components/atoms/Textarea'
import { phoneNumberMask, zipMask } from 'helpers/masks'
import { getAddressFields } from 'components/dme/forms/utils/userFormUtils'
import * as generalHelper from 'helpers/generalHelper'
import { HstPatientPaperworkDto, Order } from 'store/types'
import { tsOfficePhone } from 'constants/misc'

interface IProps {
  nextStep: () => void
  order: Order
  savePatientPaperwork: (data: Partial<HstPatientPaperworkDto>) => Promise<void>
}

const eligibilityQuestions = [
  {
    name: 'congestiveHeartFailure',
    label: 'Have you been diagnosed with congestive heart failure (CHF)?',
  },
  {
    name: 'hypoventilationSyndrome',
    label: 'Have you been diagnosed with hypoventilation syndrome?',
  },
  {
    name: 'moderateToSeverePulmonaryDisease',
    label: 'Do you have severe COPD?',
  },
  {
    name: 'historyOfStroke',
    label: 'History of stroke?',
  },
  {
    name: 'opioidOrNarcoticMedication',
    label: 'Are you currently taking an opioid or narcotic medication?',
  },
  {
    name: 'supplementalOxygenAtHome',
    label: 'Do you use supplemental oxygen at home?',
  },
  {
    name: 'implantedCardiacRhythmDevice',
    label: 'Do you have a pacemaker, defibrillator, or other implanted cardiac rhythm device?',
  },
] as const

const strokeSymptomsOptions = [
  { value: 'resolved', label: 'Resolution of symptoms' },
  { value: 'persistent', label: 'Persistent symptoms' },
] as const

const formValidation = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  dob: Yup.date().required('Required'),
  homePhone: requiredPhoneValidation,
  mobilePhone: phoneValidation,
  primaryAddressStreetLine1: Yup.string().required('Required'),
  primaryAddressCity: Yup.string().required('Required'),
  primaryAddressState: Yup.string().required('Required'),
  primaryAddressZip: Yup.string().required('Required'),
  heightFt: Yup.string().required('Required'),
  heightIn: Yup.string().required('Required'),
  weight: Yup.string().required('Required'),
  smartphone: Yup.string().required('Required'),
  adhesiveAllergies: Yup.string().required('Required'),
  congestiveHeartFailure: Yup.string().required('Required'),
  hypoventilationSyndrome: Yup.string().required('Required'),
  moderateToSeverePulmonaryDisease: Yup.string().required('Required'),
  historyOfStroke: Yup.string().required('Required'),
  strokeSymptoms: Yup.string().when('historyOfStroke', {
    is: (historyOfStroke: string) => historyOfStroke === 'yes',
    then: Yup.string().required('Required'),
  }),
  opioidOrNarcoticMedication: Yup.string().required('Required'),
  supplementalOxygenAtHome: Yup.string().required('Required'),
  implantedCardiacRhythmDevice: Yup.string().required('Required'),
  insuranceConfirmed: Yup.string().required('Required'),
  insurancePayerName: Yup.string().when('insuranceConfirmed', {
    is: (insuranceConfirmed: string) => insuranceConfirmed === 'no',
    then: Yup.string().required('Required'),
  }),
  insuranceMemberId: Yup.string().when('insuranceConfirmed', {
    is: (insuranceConfirmed: string) => insuranceConfirmed === 'no',
    then: Yup.string().required('Required'),
  }),
})

const PatientDetails = ({ nextStep, order, savePatientPaperwork }: IProps) => {
  const patientDob = order?.patient?.dob ? moment(order.patient.dob).format('MM/DD/YYYY') : ''
  const primaryPayer = order?.payers?.find((payer) => payer.position === 1)
  const savedInsurance = order?.hst?.paperwork?.data?.insurance

  const shippingAddress = useMemo(() => {
    const patient = order?.patient
    const useDelivery = !patient?.deliverySameAsPrimary && !!patient?.deliveryAddressStreetLine1

    return {
      streetLine1:
        (useDelivery ? patient?.deliveryAddressStreetLine1 : patient?.primaryAddressStreetLine1) ??
        '',
      streetLine2:
        (useDelivery ? patient?.deliveryAddressStreetLine2 : patient?.primaryAddressStreetLine2) ??
        '',
      city: (useDelivery ? patient?.deliveryAddressCity : patient?.primaryAddressCity) ?? '',
      state: (useDelivery ? patient?.deliveryAddressState : patient?.primaryAddressState) ?? '',
      zip: (useDelivery ? patient?.deliveryAddressZip : patient?.primaryAddressZip) ?? '',
    }
  }, [order?.patient])

  const initialData = useMemo(
    () => ({
      firstName: order?.hst?.paperwork?.data?.patient?.firstName ?? order?.patient?.firstName ?? '',
      lastName: order?.hst?.paperwork?.data?.patient?.lastName ?? order?.patient?.lastName ?? '',
      middleName:
        order?.hst?.paperwork?.data?.patient?.middleName ?? order?.patient?.middleName ?? '',
      dob: order?.hst?.paperwork?.data?.patient?.dob ?? patientDob ?? '',
      homePhone:
        order?.hst?.paperwork?.data?.patient?.homePhone?.replace(/^\+1/, '') ??
        order?.patient?.homePhone.replace(/^\+1/, '') ??
        '',
      mobilePhone:
        order?.hst?.paperwork?.data?.patient?.mobilePhone?.replace(/^\+1/, '') ??
        order?.patient?.mobilePhone?.replace(/^\+1/, '') ??
        '',
      address:
        order?.hst?.paperwork?.data?.patient?.primaryAddressStreetLine1 ??
        shippingAddress.streetLine1,
      primaryAddressStreetLine1:
        order?.hst?.paperwork?.data?.patient?.primaryAddressStreetLine1 ??
        shippingAddress.streetLine1,
      primaryAddressStreetLine2:
        order?.hst?.paperwork?.data?.patient?.primaryAddressStreetLine2 ??
        shippingAddress.streetLine2,
      primaryAddressCity:
        order?.hst?.paperwork?.data?.patient?.primaryAddressCity ?? shippingAddress.city,
      primaryAddressState:
        order?.hst?.paperwork?.data?.patient?.primaryAddressState ?? shippingAddress.state,
      primaryAddressZip:
        order?.hst?.paperwork?.data?.patient?.primaryAddressZip ?? shippingAddress.zip,
      heightFt: order?.hst?.paperwork?.data?.patient?.heightFt ?? order?.patient?.heightFt ?? '',
      heightIn: order?.hst?.paperwork?.data?.patient?.heightIn ?? order?.patient?.heightIn ?? '',
      weight: order?.hst?.paperwork?.data?.patient?.weight ?? order?.patient?.weight ?? '',
      smartphone: order?.hst?.paperwork?.data?.patient?.smartphone ?? '',
      adhesiveAllergies: order?.hst?.paperwork?.data?.patient?.adhesiveAllergies ?? '',
      congestiveHeartFailure:
        order?.hst?.paperwork?.data?.patient?.congestiveHeartFailure ?? '',
      hypoventilationSyndrome:
        order?.hst?.paperwork?.data?.patient?.hypoventilationSyndrome ?? '',
      moderateToSeverePulmonaryDisease:
        order?.hst?.paperwork?.data?.patient?.moderateToSeverePulmonaryDisease ?? '',
      historyOfStroke:
        order?.hst?.paperwork?.data?.patient?.historyOfStroke ?? '',
      strokeSymptoms:
        order?.hst?.paperwork?.data?.patient?.strokeSymptoms ??
        (order?.hst?.paperwork?.data?.patient?.historyOfStroke === 'yes' ? 'resolved' : ''),
      opioidOrNarcoticMedication:
        order?.hst?.paperwork?.data?.patient?.opioidOrNarcoticMedication ?? '',
      supplementalOxygenAtHome:
        order?.hst?.paperwork?.data?.patient?.supplementalOxygenAtHome ?? '',
      implantedCardiacRhythmDevice:
        order?.hst?.paperwork?.data?.patient?.implantedCardiacRhythmDevice ?? '',
      comments: '',
      insuranceConfirmed: savedInsurance?.confirmed ?? '',
      insurancePayerName: savedInsurance?.payerName ?? '',
      insuranceMemberId: savedInsurance?.memberId ?? '',
      insuranceGroupNumber: savedInsurance?.groupNumber ?? '',
      insuranceComments: savedInsurance?.comments ?? '',
    }),
    [order, patientDob, savedInsurance, shippingAddress],
  )

  return (
    <div className="container" style={{ maxWidth: 720 }}>
      <div className="mb-3 pb-3">
        <h2>Home Sleep Test (HST) Patient Registration Form</h2>
        <div className="mt-5 mb-5 pt-3 border-top">
          <p>
            Complete the Home Sleep Test Patient Registration Form to the best of your ability. All
            pages must be completed and delivered back to our office before the Home Sleep Test is
            mailed to you. Contact our staff at our office phone number {tsOfficePhone} with any
            questions. The Patient Registration Form can be hand delivered or mailed to our office
            at the address 301 NW Cole Terrace Suite 101, Lake City, FL 32055.
          </p>
        </div>
        <Formik
          enableReinitialize={true}
          validationSchema={formValidation}
          initialValues={initialData}
          onSubmit={async (values) => {
            const {
              insuranceConfirmed,
              insurancePayerName,
              insuranceMemberId,
              insuranceGroupNumber,
              insuranceComments,
              ...patient
            } = values

            try {
              await savePatientPaperwork({
                orderId: Number(order.id),
                meta: {
                  completedStep: 'patient',
                  insuranceCorrection: insuranceConfirmed === 'no',
                },
                data: {
                  patient,
                  insurance: {
                    confirmed: insuranceConfirmed as 'yes' | 'no',
                    payerName: insurancePayerName,
                    memberId: insuranceMemberId,
                    groupNumber: insuranceGroupNumber,
                    comments: insuranceComments,
                  },
                },
              })
              nextStep()
            } catch (err) {}
          }}>
          {({ values, errors, touched, handleChange, setFieldValue }) => (
            <Form id="patientDetails">
              <div className={`container`}>
                <div className="row">
                  <div className="col-sm-12 col-lg-3 form-group textGray">
                    <label htmlFor="firstName">
                      First Name
                      <span className="text-danger">*</span>
                    </label>
                    <Field
                      disabled
                      type="text"
                      name="firstName"
                      className="form-control"
                      placeholder="First Name"
                      value={values.firstName}
                      onChange={handleChange}
                      autoComplete="one-time-code"
                    />
                    {touched.firstName && errors.firstName ? (
                      <div className="text-danger">{errors.firstName}</div>
                    ) : (
                      ''
                    )}
                  </div>
                  <div className="col-sm-12 col-lg-2 form-group textGray">
                    <label htmlFor="middleName">Middle Name</label>
                    <Field
                      disabled
                      type="text"
                      name="middleName"
                      className="form-control"
                      placeholder="Middle Name"
                      value={values.middleName}
                      onChange={handleChange}
                      autoComplete="one-time-code"
                    />
                  </div>
                  <div className="col-sm-9 col-lg-4 form-group textGray">
                    <label htmlFor="lastName">
                      Last Name
                      <span className="text-danger">*</span>
                    </label>
                    <Field
                      disabled
                      type="text"
                      name="lastName"
                      className="form-control"
                      placeholder="Last Name"
                      value={values.lastName}
                      onChange={handleChange}
                      autoComplete="one-time-code"
                    />
                    {touched.lastName && errors.lastName ? (
                      <div className="text-danger">{errors.lastName}</div>
                    ) : (
                      ''
                    )}
                  </div>
                  <div className="col-sm-12 col-lg-3 form-group textGray">
                    <label htmlFor="dob">
                      Date Of Birth
                      <span className="text-danger">*</span>
                    </label>
                    <InputMask
                      disabled
                      value={values.dob ?? ' '}
                      className="form-control"
                      name="dob"
                      onChange={handleChange}
                      mask="99/99/9999"
                    />
                    {touched.dob && errors.dob ? <div className="text-danger">Required</div> : ''}
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12 col-lg-4 form-group textGray">
                    <label htmlFor="height">
                      Height
                      <span className="text-danger">*</span>
                    </label>
                    <div className="d-flex">
                      <div className="input-group mr-2">
                        <Field
                          type="text"
                          name="heightFt"
                          className="form-control"
                          placeholder="Feet"
                          value={values.heightFt}
                          onChange={handleChange}
                        />
                        <div className="input-group-append">
                          <span className="input-group-text">ft</span>
                        </div>
                      </div>
                      <div className="input-group">
                        <Field
                          type="text"
                          name="heightIn"
                          className="form-control"
                          placeholder="Inches"
                          value={values.heightIn}
                          onChange={handleChange}
                        />
                        <div className="input-group-append">
                          <span className="input-group-text">in</span>
                        </div>
                      </div>
                    </div>
                    {(touched.heightFt && errors.heightFt) ||
                    (touched.heightIn && errors.heightIn) ? (
                      <div className="text-danger">{errors.heightFt || errors.heightIn}</div>
                    ) : null}
                  </div>
                  <div className="col-sm-12 col-lg-4 form-group textGray">
                    <label htmlFor="weight">
                      Weight
                      <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <Field
                        type="text"
                        name="weight"
                        className="form-control"
                        placeholder="Weight"
                        value={values.weight}
                        onChange={handleChange}
                      />
                      <div className="input-group-append">
                        <span className="input-group-text">lbs</span>
                      </div>
                    </div>
                    {touched.weight && errors.weight ? (
                      <div className="text-danger">{errors.weight}</div>
                    ) : null}
                  </div>
                  <div className="col-sm-12 col-lg-4 form-group textGray">
                    <label htmlFor="smartphone">
                      Do you have a smartphone?
                      <span className="text-danger">*</span>
                    </label>
                    <Field
                      className="form-control"
                      as="select"
                      name="smartphone"
                      value={values.smartphone}
                      onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="iphone">iPhone</option>
                      <option value="android">Android</option>
                      <option value="none">No smart phone</option>
                    </Field>
                    {touched.smartphone && errors.smartphone ? (
                      <div className="text-danger">{errors.smartphone}</div>
                    ) : null}
                  </div>
                  <div className="col-sm-12 col-lg-4 form-group textGray">
                    <label htmlFor="adhesiveAllergies">
                      Adhesive Allergies
                      <span className="text-danger">*</span>
                    </label>
                    <Field
                      className="form-control"
                      as="select"
                      name="adhesiveAllergies"
                      value={values.adhesiveAllergies}
                      onChange={handleChange}>
                      <option value="">Select...</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </Field>
                    {touched.adhesiveAllergies && errors.adhesiveAllergies ? (
                      <div className="text-danger">{errors.adhesiveAllergies}</div>
                    ) : null}
                  </div>
                </div>

                <div className="mb-3">
                  <div className="row">
                    {eligibilityQuestions.map((question) => (
                      <div
                        className="col-sm-12 col-lg-6 form-group textGray flex-column justify-content-end"
                        css={{display: "flex"}}
                        key={question.name}>
                        <label htmlFor={question.name}>
                          {question.label}
                          <span className="text-danger">*</span>
                        </label>
                        <Field
                          className="form-control"
                          as="select"
                          name={question.name}
                          value={values[question.name]}
                          onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                            handleChange(event)

                            if (question.name === 'historyOfStroke') {
                              setFieldValue(
                                'strokeSymptoms',
                                event.target.value === 'yes' ? 'resolved' : '',
                              )
                            }
                          }}>
                          <option value="">Select...</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </Field>
                        {touched[question.name] && errors[question.name] ? (
                          <div className="text-danger">{errors[question.name]}</div>
                        ) : null}
                        {question.name === 'historyOfStroke' && values.historyOfStroke === 'yes' ? (
                          <div
                            css={{
                              position: 'absolute',
                              bottom: '-20px',
                              width: '100%',
                              '@media (max-width: 991.98px)': {
                                position: 'static',
                                marginTop: '0.5rem',
                              },
                            }}>
                            <div className="row">
                              {strokeSymptomsOptions.map((option) => (
                                <div className="col-6" key={option.value}>
                                  <div className="form-check">
                                    <Field
                                      className="form-check-input"
                                      css={{marginTop: '0.15rem'}}
                                      type="radio"
                                      id={`strokeSymptoms-${option.value}`}
                                      name="strokeSymptoms"
                                      value={option.value}
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`strokeSymptoms-${option.value}`}>
                                      {option.label}
                                    </label>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {touched.strokeSymptoms && errors.strokeSymptoms ? (
                              <div className="text-danger">{errors.strokeSymptoms}</div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12 col-lg-3 form-group textGray">
                    <label htmlFor="homePhone">
                      Home Number
                      <span className="text-danger">*</span>
                    </label>
                    <MaskInput
                      disabled
                      value={values.homePhone}
                      onChange={handleChange}
                      name="homePhone"
                      mask={phoneNumberMask}
                      placeholder="(###) ###-####"
                      autoComplete="off"
                    />
                    {touched.homePhone && errors.homePhone ? (
                      <div className="text-danger">{errors.homePhone}</div>
                    ) : (
                      ''
                    )}
                  </div>
                  <div className="col-sm-12 col-lg-3 form-group textGray">
                    <label htmlFor="mobilePhone">Mobile Number</label>
                    <MaskInput
                      disabled
                      value={values.mobilePhone}
                      onChange={handleChange}
                      name="mobilePhone"
                      mask={phoneNumberMask}
                      placeholder="(###) ###-####"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              <div className={`container`}>
                <div className="row">
                  <div className="col-sm-12 col-lg-6">
                    <div className="row">
                      <div className="col-12 form-group textGray">
                        <label htmlFor="primaryAddressStreetLine1">
                          {generalHelper.t('Shipping Address')}
                          <span className="text-danger">*</span>
                        </label>
                        <GoogleAutocomplete
                          id="address"
                          name="address"
                          onInputChanged={(v) => setFieldValue('primaryAddressStreetLine1', v)}
                          onChange={(place) => {
                            const { address, city, state, zip } = getAddressFields(place, 'address')
                            console.log(address, city, state, zip)
                            setFieldValue('primaryAddressStreetLine1', address)
                            setFieldValue('primaryAddressCity', city)
                            setFieldValue('primaryAddressState', state)
                            setFieldValue('primaryAddressZip', zip)
                          }}
                        />
                        {touched.primaryAddressStreetLine1 && errors.primaryAddressStreetLine1 ? (
                          <div className="text-danger">{errors.primaryAddressStreetLine1}</div>
                        ) : null}
                      </div>
                      <div className="col-12 form-group textGray">
                        <Field
                          type="text"
                          name="primaryAddressStreetLine2"
                          className="form-control"
                          placeholder="Suite / Apt"
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-4 form-group textGray">
                        <label htmlFor="primaryAddressCity">
                          City
                          <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="text"
                          name="primaryAddressCity"
                          className="form-control"
                          placeholder="City"
                        />
                        {touched.primaryAddressCity && errors.primaryAddressCity ? (
                          <div className="text-danger">{errors.primaryAddressCity}</div>
                        ) : null}
                      </div>
                      <div className="col-4 form-group textGray">
                        <label htmlFor="primaryAddressState">
                          State
                          <span className="text-danger">*</span>
                        </label>
                        <Field
                          className="form-control"
                          as="select"
                          name="primaryAddressState"
                          placeholder="State">
                          {states &&
                            states.map((s, i) => (
                              <option key={i} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                        </Field>
                        {touched.primaryAddressState && errors.primaryAddressState ? (
                          <div className="text-danger">{errors.primaryAddressState}</div>
                        ) : null}
                      </div>
                      <div className="col-4 form-group textGray">
                        <label htmlFor="primaryAddressZip">
                          Zip Code
                          <span className="text-danger">*</span>
                        </label>
                        <MaskInput
                          value={values.primaryAddressZip}
                          onChange={handleChange}
                          name="primaryAddressZip"
                          mask={zipMask}
                          placeholder="#####-####"
                        />

                        {touched.primaryAddressZip && errors.primaryAddressZip ? (
                          <div className="text-danger">{errors.primaryAddressZip}</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={`container`}>
                <h5 className="border-bottom pb-2 mb-3">Insurance</h5>
                <div className="row">
                  <div className="col-12 form-group textGray">
                    <div className="mb-2">We have the following insurance on file for you:</div>
                    <div className="mb-3 p-3" css={{ background: '#f4f6f8', borderRadius: 5 }}>
                      <div>
                        <strong>Insurance company:</strong> {primaryPayer?.name || '-'}
                      </div>
                      <div>
                        <strong>Member ID:</strong> {primaryPayer?.memberId || '-'}
                      </div>
                      {primaryPayer?.groupId ? (
                        <div>
                          <strong>Group ID:</strong> {primaryPayer.groupId}
                        </div>
                      ) : null}
                    </div>
                    <label htmlFor="insuranceConfirmed">
                      Is this insurance information correct?
                      <span className="text-danger">*</span>
                    </label>
                    <Field
                      className="form-control"
                      as="select"
                      name="insuranceConfirmed"
                      value={values.insuranceConfirmed}
                      onChange={handleChange}
                      css={{ maxWidth: 300 }}>
                      <option value="">Select...</option>
                      <option value="yes">Yes, this is correct</option>
                      <option value="no">No, my insurance is different</option>
                    </Field>
                    {touched.insuranceConfirmed && errors.insuranceConfirmed ? (
                      <div className="text-danger">{errors.insuranceConfirmed}</div>
                    ) : null}
                  </div>
                </div>
                {values.insuranceConfirmed === 'no' && (
                  <>
                    <div className="row">
                      <div className="col-12 textGray mb-3">
                        Please enter your current insurance below. We will review it and follow up
                        with you before your test is shipped.
                      </div>
                      <div className="col-sm-12 col-lg-4 form-group textGray">
                        <label htmlFor="insurancePayerName">
                          Insurance Company
                          <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="text"
                          name="insurancePayerName"
                          className="form-control"
                          placeholder="Insurance Company"
                        />
                        {touched.insurancePayerName && errors.insurancePayerName ? (
                          <div className="text-danger">{errors.insurancePayerName}</div>
                        ) : null}
                      </div>
                      <div className="col-sm-12 col-lg-4 form-group textGray">
                        <label htmlFor="insuranceMemberId">
                          Member ID
                          <span className="text-danger">*</span>
                        </label>
                        <Field
                          type="text"
                          name="insuranceMemberId"
                          className="form-control"
                          placeholder="Member ID"
                        />
                        {touched.insuranceMemberId && errors.insuranceMemberId ? (
                          <div className="text-danger">{errors.insuranceMemberId}</div>
                        ) : null}
                      </div>
                      <div className="col-sm-12 col-lg-4 form-group textGray">
                        <label htmlFor="insuranceGroupNumber">Group ID</label>
                        <Field
                          type="text"
                          name="insuranceGroupNumber"
                          className="form-control"
                          placeholder="Group ID"
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 form-group textGray">
                        <Field
                          component={TextAreaInput}
                          name="insuranceComments"
                          label="Anything else we should know about your insurance?"
                          rows={2}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className={`container`}>
                <div className="row">
                  <div className="col-12 form-group textGray">
                    <Field
                      component={TextAreaInput}
                      name="comments"
                      label="Comments / Special Instructions"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
              <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="primary" type="submit" css={{}}>
                  Submit
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default PatientDetails
